/**
 * RepConnect - Sales Representative Provider Discovery & Library Management
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 *
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Star, MapPin, Clock, Coffee, Phone, User,
  Bookmark, Check, Users, Calendar as CalendarIcon,
  Plus, X, Edit3, Mail, UserPlus, AlertCircle
} from 'lucide-react';
import { UserStorage } from '../utils/userStorage';
import { ProviderLibrary } from '../utils/providerLibrary';
import { UnifiedProviderSearch } from '../utils/unifiedProviderSearch';
import { customerFavoritesService } from '../utils/customerFavoritesService';
import { logger } from '../utils/logger';

const FindProvidersView = ({ setSelectedCustomer, setCurrentView, currentUser: passedCurrentUser }) => {
  const [searchName, setSearchName] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [currentUser, setCurrentUser] = useState(passedCurrentUser || null);
  const [filters, setFilters] = useState({
    specialty: 'all',
    distance: '10',
    availability: 'all'
  });

  // Provider Library State
  const [savedProviders, setSavedProviders] = useState([]);
  const [showSavedProviders, setShowSavedProviders] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Unified Search State
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  // Always use unified search (production only) - no toggle needed
  const [searchMetadata, setSearchMetadata] = useState({
    registeredCount: 0,
    npiCount: 0,
    total: 0,
    hasMore: false
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20,
    total: 0,
    hasMore: false
  });

  // Manual Invite Modal State
  const [showManualInviteModal, setShowManualInviteModal] = useState(false);
  const [manualInviteData, setManualInviteData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    specialty: '',
    practice: ''
  });
  const [manualInviteError, setManualInviteError] = useState('');

  useEffect(() => {
    // Use passed user or get from storage
    const user = passedCurrentUser || UserStorage.getCurrentUser();
    if (user) {
      setCurrentUser(user);

      // Load provider library data for sales reps and managers
      // Check both userType formats (camelCase and underscore)
      const userType = user.userType || user.user_type;
      if (userType === 'sales' || userType === 'manager') {
        loadProviderLibrary(user.id);
      }
    }
  }, [passedCurrentUser]);

  // Reload favorites when currentUser changes (e.g., after login)
  useEffect(() => {
    if (currentUser && currentUser.id) {
      const userType = currentUser.userType || currentUser.user_type;
      if (userType === 'sales' || userType === 'manager') {
        loadProviderLibrary(currentUser.id);
      }
    }
  }, [currentUser?.id]);

  const loadProviderLibrary = async (salesRepId) => {
    try {
      logger.debug('Loading customer library for sales rep', { salesRepId });
      // Load favorites from Supabase first
      const favorites = await customerFavoritesService.getFavorites(salesRepId);
      logger.debug('Loaded favorites from Supabase', { count: favorites?.length });

      if (favorites && favorites.length > 0) {
        setSavedProviders(favorites);
      } else {
        logger.debug('No Supabase favorites, checking localStorage', { salesRepId });
        // Fallback to localStorage if no Supabase favorites
        const saved = ProviderLibrary.getSavedProviders(salesRepId);
        logger.debug('localStorage favorites loaded', { count: saved?.length });
        setSavedProviders(saved);
      }
    } catch (error) {
      logger.error('Error loading customer library', { error: error.message, salesRepId });
      // Fallback to localStorage on error
      const saved = ProviderLibrary.getSavedProviders(salesRepId);
      setSavedProviders(saved);
    }
  };

  const handleSaveProvider = async (provider) => {
    // Check both userType formats (camelCase and underscore)
    const userType = currentUser?.userType || currentUser?.user_type;
    if (!currentUser || (userType !== 'sales' && userType !== 'manager')) return;

    try {
      const isAlreadySaved = isProviderSaved(provider.id);
      logger.debug('Saving customer', { providerName: provider.name, isAlreadySaved });

      if (isAlreadySaved) {
        logger.debug('Removing from favorites', { providerId: provider.id });
        await customerFavoritesService.removeFavorite(currentUser.id, provider.id);
        // Optimistically update UI
        setSavedProviders(prev => prev.filter(p => p.id !== provider.id && p.customerId !== provider.id));
        setSaveStatus(`${provider.name} removed from your library`);
      } else {
        logger.debug('Adding to favorites', { providerId: provider.id, providerName: provider.name });
        const result = await customerFavoritesService.addFavorite(currentUser.id, provider.id, null, provider);
        logger.debug('Add favorite result', { success: !!result });
        // Optimistically update UI
        setSavedProviders(prev => [...prev, { ...provider, customerId: provider.id, isFavorite: true }]);
        setSaveStatus(`${provider.name} saved to your library`);
      }

      // Reload from Supabase to ensure sync
      logger.debug('Reloading library after save', { userId: currentUser.id });
      await loadProviderLibrary(currentUser.id);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error saving customer', { error: error.message, providerId: provider.id });
      setSaveStatus('Error saving customer');
      // Reload on error to ensure correct state
      await loadProviderLibrary(currentUser.id);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const isProviderSaved = (providerId) => {
    // Check if provider is in the savedProviders list (already loaded from Supabase)
    return savedProviders.some(p => p.id === providerId || p.customerId === providerId);
  };

  const handleScheduleFromLibrary = (provider) => {
    // Update last contact tracking when scheduling from library
    if (currentUser && currentUser.userType === 'sales') {
      ProviderLibrary.updateProvider(currentUser.id, provider.id, {
        lastContactDate: new Date().toISOString(),
        nextFollowUp: (() => {
          const followUp = new Date();
          followUp.setDate(followUp.getDate() + 14); // 2 weeks follow-up
          return followUp.toISOString();
        })()
      });
    }

    setSelectedCustomer(provider);
    setCurrentView('book-meeting');
  };

  const handleViewProfile = async (provider) => {
    try {
      // For NPI providers, try to enhance with CMS data
      if (provider.source === 'NPI Registry' && provider.npi) {
        // Try to get enhanced data from CMS API (will work in server environment)
        // For now, just use the existing provider data
        setSelectedCustomer(provider);
      } else {
        setSelectedCustomer(provider);
      }
      setCurrentView('customer-profile');
    } catch (error) {
      logger.error('Error enhancing provider profile', { error: error.message, providerId: provider.id });
      // Fallback to basic provider data
      setSelectedCustomer(provider);
      setCurrentView('customer-profile');
    }
  };

  // Unified provider search with database + NPI integration
  const searchUnifiedProviders = useCallback(async (resetPagination = true) => {
    if (!searchName.trim() && !searchLocation.trim() && filters.specialty === 'all') {
      setSearchResults([]);
      setSearchError('');
      setSearchMetadata({ registeredCount: 0, npiCount: 0, total: 0, hasMore: false });
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      const searchParams = {
        name: searchName,
        location: searchLocation,
        specialty: filters.specialty,
        limit: pagination.limit,
        offset: resetPagination ? 0 : pagination.offset
      };

      const results = await UnifiedProviderSearch.searchProviders(searchParams);

      if (resetPagination) {
        setSearchResults(results.providers);
        setSearchMetadata({
          registeredCount: results.registeredCount,
          npiCount: results.npiCount,
          total: results.total,
          hasMore: results.hasMore
        });
        setPagination({
          offset: 0,
          limit: pagination.limit,
          total: results.total,
          hasMore: results.hasMore
        });
      } else {
        // Append results for pagination (primarily for NPI results)
        setSearchResults(prev => [...prev, ...results.providers]);
        setSearchMetadata(prev => ({
          ...prev,
          npiCount: prev.npiCount + results.npiCount,
          total: prev.total + results.providers.length,
          hasMore: results.hasMore
        }));
        setPagination(prev => ({
          ...prev,
          offset: results.offset + results.providers.length,
          total: prev.total + results.providers.length,
          hasMore: results.hasMore
        }));
      }

    } catch (error) {
      logger.error('Unified search error', { error: error.message, searchName, searchLocation, specialty: filters.specialty });
      setSearchError(`Search failed: ${error.message}`);
      setSearchResults([]);
      setSearchMetadata({ registeredCount: 0, npiCount: 0, total: 0, hasMore: false });
    } finally {
      setIsSearching(false);
    }
  }, [searchName, searchLocation, filters.specialty, pagination.limit, pagination.offset]);

  // Load more results for pagination
  const loadMoreProviders = () => {
    if (!isSearching && pagination.hasMore) {
      searchUnifiedProviders(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (showSavedProviders) return;

    const timeoutId = setTimeout(() => {
      searchUnifiedProviders(true);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchName, searchLocation, filters.specialty, showSavedProviders, searchUnifiedProviders]);

  const getDisplayProviders = () => {
    if (showSavedProviders) {
      // Show all saved providers from library
      if (searchName) {
        // Filter saved providers by search term
        const term = searchName.toLowerCase();
        return savedProviders.filter(provider =>
          provider.name.toLowerCase().includes(term) ||
          (provider.specialty && provider.specialty.toLowerCase().includes(term)) ||
          (provider.hospital && provider.hospital.toLowerCase().includes(term))
        );
      }
      return savedProviders;
    } else {
      // Show unified search results (database + NPI)
      return searchResults;
    }
  };

  // Email validation helper
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle manual invite submission
  const handleManualInvite = () => {
    setManualInviteError('');

    // Validate required fields
    if (!manualInviteData.firstName.trim() || !manualInviteData.lastName.trim()) {
      setManualInviteError('First and last name are required');
      return;
    }

    if (!manualInviteData.email.trim()) {
      setManualInviteError('Email address is required');
      return;
    }

    if (!validateEmail(manualInviteData.email)) {
      setManualInviteError('Please enter a valid email address');
      return;
    }

    // Create a manual provider object and navigate to booking
    const manualProvider = {
      id: `manual_${Date.now()}`,
      name: `Dr. ${manualInviteData.firstName} ${manualInviteData.lastName}`.trim(),
      firstName: manualInviteData.firstName,
      lastName: manualInviteData.lastName,
      email: manualInviteData.email,
      specialty: manualInviteData.specialty || 'Not specified',
      hospital: manualInviteData.practice || 'Not specified',
      address: 'Not specified',
      phone: 'Not specified',
      rating: 5.0,
      preferredTime: '12:00 PM',
      isManualInvite: true,
      isRegistered: false,
      source: 'Manual Invite'
    };

    // Close modal and navigate
    setShowManualInviteModal(false);
    setManualInviteData({ firstName: '', lastName: '', email: '', specialty: '', practice: '' });
    setSelectedCustomer(manualProvider);
    setCurrentView('book-meeting');
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-2xl font-bold text-gray-800">
            {showSavedProviders ? 'My Customer Library' : 'Find Customers'}
          </h2>

          {currentUser && ((currentUser.userType === 'sales' || currentUser.userType === 'manager') || (currentUser.user_type === 'sales' || currentUser.user_type === 'manager')) && (
            <div className="flex items-center justify-between w-full sm:w-auto gap-4">
              {/* Segmented Toggle - All / Favorites */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setShowSavedProviders(false)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    !showSavedProviders
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setShowSavedProviders(true)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                    showSavedProviders
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  <span>Favorites</span>
                  {savedProviders.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {savedProviders.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Manual Invite Button */}
              <button
                onClick={() => setShowManualInviteModal(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                title="Manually invite a provider not found in search"
              >
                <Plus className="w-4 h-4" />
                <span>Send an Invite</span>
              </button>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {saveStatus && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center space-x-2">
            <Check className="w-5 h-5" />
            <span>{saveStatus}</span>
          </div>
        )}

        {searchError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-2">
            <X className="w-5 h-5" />
            <span>{searchError}</span>
          </div>
        )}

        {!showSavedProviders && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg flex items-center space-x-2">
            <Search className="w-5 h-5" />
            <span>
              Searching RepConnect database and NPI Registry.
              {searchMetadata.total > 0 && (
                <span className="ml-1">
                  Found {searchMetadata.registeredCount} registered and {searchMetadata.npiCount} directory customers.
                </span>
              )}
            </span>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col gap-4">
            {/* Separated Search Fields */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <User className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none z-10" style={{ left: '12px' }} />
                  <input
                    type="text"
                    placeholder={showSavedProviders ? "Search saved providers..." : "Name (e.g., Smith)"}
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="w-full pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    style={{ paddingLeft: '44px' }}
                    title="Enter doctor's first or last name"
                  />
                </div>
              </div>

              {!showSavedProviders && (
                <div className="flex-1">
                  <div className="relative">
                    <MapPin className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none z-10" style={{ left: '12px' }} />
                    <input
                      type="text"
                      placeholder="Location (city, state, ZIP)"
                      value={searchLocation}
                      onChange={(e) => setSearchLocation(e.target.value)}
                      className="w-full pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                      style={{ paddingLeft: '44px' }}
                      title="Examples: 'Los Angeles CA', 'Iowa', '90210'"
                    />
                  </div>
                </div>
              )}

              {!showSavedProviders && (
                <>
                  <select
                    value={filters.specialty}
                    onChange={(e) => setFilters({...filters, specialty: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
                    title="Optional: filter by specialty (search field can also detect specialties)"
                  >
                    <option value="all">All Specialties</option>
                    <option value="anesthesiology">Anesthesiology</option>
                    <option value="cardiology">Cardiology</option>
                    <option value="dermatology">Dermatology</option>
                    <option value="emergency">Emergency Medicine</option>
                    <option value="endocrinology">Endocrinology</option>
                    <option value="family">Family Medicine</option>
                    <option value="gastroenterology">Gastroenterology</option>
                    <option value="infectious">Infectious Disease</option>
                    <option value="internal">Internal Medicine</option>
                    <option value="nephrology">Nephrology</option>
                    <option value="neurology">Neurology</option>
                    <option value="obstetrics">Obstetrics & Gynecology</option>
                    <option value="oncology">Oncology</option>
                    <option value="ophthalmology">Ophthalmology</option>
                    <option value="orthopedics">Orthopedics</option>
                    <option value="otolaryngology">Otolaryngology (ENT)</option>
                    <option value="pathology">Pathology</option>
                    <option value="pediatrics">Pediatrics</option>
                    <option value="plastic">Plastic Surgery</option>
                    <option value="psychiatry">Psychiatry</option>
                    <option value="pulmonology">Pulmonology</option>
                    <option value="radiology">Radiology</option>
                    <option value="rheumatology">Rheumatology</option>
                    <option value="surgery">Surgery</option>
                    <option value="urology">Urology</option>
                  </select>
                </>
              )}
            </div>

            {/* Search Examples */}
            {!showSavedProviders && (searchName || searchLocation || filters.specialty !== 'all') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium mb-1">Search tips:</p>
                <div className="text-xs text-blue-700 space-y-1">
                  <div><strong>Name field:</strong> Enter doctor's first or last name (e.g., "Smith", "Dr. Jones")</div>
                  <div><strong>Location field:</strong> Enter city, state, or ZIP code (e.g., "Los Angeles CA", "Iowa", "90210")</div>
                  <div><strong>Specialty:</strong> Select from dropdown to filter by medical specialty</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isSearching && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Searching physicians...</span>
        </div>
      )}

      {/* Provider Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {getDisplayProviders().map(customer => (
          <div key={customer.id} className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow ${
            customer.isRegistered ? 'border-l-4 border-blue-500' : 'border-l-4 border-gray-300'
          }`}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-semibold text-gray-800">{customer.name}</h3>
                    {customer.source && (
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        customer.isRegistered
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}>
                        {customer.isRegistered ? 'RepConnect Member' : 'NPI Directory'}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">{customer.specialty} • {customer.hospital}</p>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                    <span className="ml-1 font-semibold">{customer.rating}</span>
                  </div>

                  {/* Save/Unsave Button for Sales Reps */}
                  {currentUser && ((currentUser.userType === 'sales' || currentUser.userType === 'manager') || (currentUser.user_type === 'sales' || currentUser.user_type === 'manager')) && (
                    <button
                      onClick={() => handleSaveProvider(customer)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                        isProviderSaved(customer.id)
                          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={isProviderSaved(customer.id) ? 'Remove from library' : 'Add to library'}
                    >
                      {isProviderSaved(customer.id) ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span className="text-sm font-medium hidden sm:inline">Saved</span>
                        </>
                      ) : (
                        <>
                          <Bookmark className="w-4 h-4" />
                          <span className="text-sm font-medium hidden sm:inline">Add to Library</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-600 flex items-center">
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{customer.address}</span>
                </p>

                {/* Show phone number for real providers and saved customers */}
                {(customer.source === 'CMS NPI Registry' || customer.source === 'Saved' || showSavedProviders) && customer.phone && customer.phone !== 'Contact for phone' && (
                  <p className="text-sm text-gray-600 flex items-center">
                    <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="font-medium">{customer.phone}</span>
                  </p>
                )}

                <p className="text-sm text-gray-600 flex items-center">
                  <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>Prefers {customer.preferredTime}</span>
                </p>

                {/* Show credentials for real providers */}
                {customer.source === 'CMS NPI Registry' && customer.credential && (
                  <p className="text-sm text-blue-600 flex items-center">
                    <User className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="font-medium">{customer.credential}</span>
                  </p>
                )}

                {/* Show favorites only for mock data */}
                {customer.favoriteRestaurants && (
                  <p className="text-sm text-gray-600 flex items-center">
                    <Coffee className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>Favorites: {customer.favoriteRestaurants.join(', ')}</span>
                  </p>
                )}

                {/* Show saved provider metadata */}
                {showSavedProviders && customer.savedAt && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 flex items-center">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      Saved {new Date(customer.savedAt).toLocaleDateString()}
                    </p>
                    {customer.notes && (
                      <p className="text-xs text-gray-600 mt-1 flex items-start">
                        <Edit3 className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                        {customer.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => {
                    if (showSavedProviders) {
                      handleScheduleFromLibrary(customer);
                    } else {
                      setSelectedCustomer(customer);
                      setCurrentView('book-meeting');
                    }
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                    customer.isRegistered
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  <CalendarIcon className="w-4 h-4" />
                  <span>
                    {customer.isRegistered ? 'Schedule Meeting' : 'Invite to RepConnect'}
                  </span>
                </button>

                <div className="flex gap-2">
                  {/* Quick Actions for Saved Providers */}
                  {showSavedProviders && currentUser && currentUser.userType === 'sales' && customer.lastContactDate && (
                    <button
                      onClick={() => {
                        // Quick follow-up scheduling
                        const nextWeek = new Date();
                        nextWeek.setDate(nextWeek.getDate() + 7);
                        setSelectedCustomer({
                          ...customer,
                          suggestedDate: nextWeek.toISOString().split('T')[0],
                          suggestedTime: customer.preferredTime || '12:00 PM'
                        });
                        setCurrentView('book-meeting');
                      }}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center space-x-1"
                      title="Quick follow-up meeting"
                    >
                      <Clock className="w-4 h-4" />
                      <span className="hidden sm:inline">Follow-up</span>
                    </button>
                  )}

                  <button
                    onClick={() => handleViewProfile(customer)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1 whitespace-nowrap"
                  >
                    <span>View Profile</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {!showSavedProviders && pagination.hasMore && !isSearching && (
        <div className="flex justify-center mt-6">
          <button
            onClick={loadMoreProviders}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Load More Providers</span>
          </button>
        </div>
      )}

      {/* Search Results Summary */}
      {!showSavedProviders && searchResults.length > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600">
          <div>
            Showing {searchResults.length} providers total
            {pagination.hasMore && ' • More results available'}
          </div>
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 mr-2">
              {searchMetadata.registeredCount} RepConnect Members
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
              {searchMetadata.npiCount} NPI Directory
            </span>
          </div>
        </div>
      )}

      {getDisplayProviders().length === 0 && !isSearching && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {showSavedProviders ? 'No Saved Customers' : 'No Customers Found'}
          </h3>
          <p className="text-gray-600 mb-4">
            {showSavedProviders
              ? 'You haven\'t saved any customers yet. Switch to search mode to find and save customers.'
              : 'Try entering a provider name or location to search RepConnect database and NPI registry'
            }
          </p>
          {/* Manual invite option when no results */}
          {!showSavedProviders && (
            <div className="mt-4">
              <p className="text-gray-600 mb-3">Can't find them?</p>
              <button
                onClick={() => setShowManualInviteModal(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors inline-flex items-center space-x-2"
              >
                <UserPlus className="w-4 h-4" />
                <span>Send them an Invite</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Manual Invite Modal */}
      {showManualInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Manual Invite</h3>
                </div>
                <button
                  onClick={() => {
                    setShowManualInviteModal(false);
                    setManualInviteError('');
                    setManualInviteData({ firstName: '', lastName: '', email: '', specialty: '', practice: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Warning Banner */}
            <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Search first to avoid duplicates</p>
                <p className="text-amber-700">
                  Please make sure you've searched for this provider first. Manual invites should only be used for providers not found in the NPI database.
                </p>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Error Message */}
              {manualInviteError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center space-x-2">
                  <X className="w-5 h-5" />
                  <span>{manualInviteError}</span>
                </div>
              )}

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={manualInviteData.firstName}
                    onChange={(e) => setManualInviteData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="John"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={manualInviteData.lastName}
                    onChange={(e) => setManualInviteData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={manualInviteData.email}
                    onChange={(e) => setManualInviteData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="doctor@practice.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Optional Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specialty <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={manualInviteData.specialty}
                  onChange={(e) => setManualInviteData(prev => ({ ...prev, specialty: e.target.value }))}
                  placeholder="e.g., Cardiology, Family Medicine"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Practice/Hospital <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={manualInviteData.practice}
                  onChange={(e) => setManualInviteData(prev => ({ ...prev, practice: e.target.value }))}
                  placeholder="e.g., City Medical Center"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t bg-gray-50 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => {
                  setShowManualInviteModal(false);
                  setManualInviteError('');
                  setManualInviteData({ firstName: '', lastName: '', email: '', specialty: '', practice: '' });
                }}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleManualInvite}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>Continue to Invite</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FindProvidersView;